import React from 'react';
import { Card } from './ui/card';
import { Trash2, CheckCircle, Package } from 'lucide-react';
import { Button } from './ui/button';

const PendingRecipesList = ({ recipes, onValidateAll, onDeleteRecipe, validating }) => {
  if (recipes.length === 0) return null;

  const totalAgglos = recipes.reduce((acc, r) => {
    if (!acc[r.agglo_type]) acc[r.agglo_type] = 0;
    acc[r.agglo_type] += r.agglo_count;
    return acc;
  }, {});

  const totalCost = recipes.reduce((acc, r) => acc + r.total_cost, 0);

  const getAggloLabel = (type) => {
    const labels = {
      'agglo_10': 'Agglo de 10',
      'agglo_15': 'Agglo de 15',
      'agglo_20': 'Agglo de 20',
      'agglo_hourdis_16': 'Agglo Hourdis 16',
      'agglo_plein_15': 'Agglo Plein 15'
    };
    return labels[type] || type;
  };

  return (
    <Card className="bg-gradient-to-r from-[#FDC800] to-[#f0b700] border-0 rounded-sm shadow-xl p-6" data-testid="pending-recipes-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-2xl font-bold text-[#1B3268]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            Recettes en attente de validation
          </h3>
          <p className="text-sm text-[#1B3268]/70 mt-1">{recipes.length} recette(s) prête(s) pour la production</p>
        </div>
        <Button
          onClick={onValidateAll}
          disabled={validating}
          data-testid="validate-daily-production-button"
          className="bg-[#1B3268] hover:bg-[#152a55] text-white font-bold rounded-sm shadow-lg h-12 px-6 text-base"
        >
          <CheckCircle className="w-5 h-5 mr-2" />
          {validating ? 'Validation...' : 'Valider Production Journalière'}
        </Button>
      </div>

      {/* Liste détaillée des recettes avec suppression */}
      <div className="bg-white/90 backdrop-blur-sm rounded-sm mb-4 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-[#1B3268]/10 text-[#1B3268] text-sm">
              <th className="p-3 text-left">Type</th>
              <th className="p-3 text-right">Quantité</th>
              <th className="p-3 text-right">Coût</th>
              <th className="p-3 text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {recipes.map((recipe) => (
              <tr key={recipe.id} className="border-t border-[#1B3268]/10 hover:bg-[#1B3268]/5">
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-[#1B3268]/60" />
                    <span className="font-medium text-[#1B3268]">{getAggloLabel(recipe.agglo_type)}</span>
                  </div>
                </td>
                <td className="p-3 text-right font-bold text-[#1B3268]">{recipe.agglo_count} unités</td>
                <td className="p-3 text-right text-[#1B3268]">{recipe.total_cost?.toLocaleString('fr-FR')} FCFA</td>
                <td className="p-3 text-center">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDeleteRecipe && onDeleteRecipe(recipe.id)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    data-testid={`delete-recipe-${recipe.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Totaux par type */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        {Object.entries(totalAgglos).map(([type, count]) => (
          <div key={type} className="bg-white/90 backdrop-blur-sm p-4 rounded-sm" data-testid={`pending-${type}`}>
            <p className="text-xs text-[#1B3268]/60 uppercase tracking-wider mb-1">{getAggloLabel(type)}</p>
            <p className="text-3xl font-bold text-[#1B3268]">{count}</p>
          </div>
        ))}
      </div>

      <div className="bg-white/90 backdrop-blur-sm p-4 rounded-sm">
        <div className="flex justify-between items-center">
          <span className="text-[#1B3268] font-medium">Coût Total Estimé</span>
          <span className="text-2xl font-bold text-[#1B3268]">{totalCost.toLocaleString('fr-FR', { minimumFractionDigits: 0 })} FCFA</span>
        </div>
      </div>

      <div className="mt-4">
        <p className="text-xs text-[#1B3268]/70">
          ⚠️ La validation mettra à jour les stocks de matières premières et ajoutera les agglos produits
        </p>
      </div>
    </Card>
  );
};

export default PendingRecipesList;
